import React, { useState, useMemo } from 'react';
import { View, ScrollView } from 'react-native';
import { interpolateBlues, interpolateGnBu, schemeCategory10 } from 'd3-scale-chromatic';
import LineChartScreen from '../common/charts/lineChart-builder';
import PaperTableDynamic from '../common/controls/TableProvider';
import PieChartComp from '../common/charts/piChart-builder';
import { getItemById } from '../common/service/dataOperations';
import { useAuth } from '../../../providers/AuthProvider';
import { useClient } from '../../../providers/ClientProvider';
import { scaleOrdinal } from 'd3-scale';
import { Loader } from '../../Common/Loader';
import { constructData, CommonStyles, GetConsolidatedLog } from './ConsolidatedController';
import { TabScreen, Tabs, TabsProvider } from 'react-native-paper-tabs';
import { colorType3, colorType5, logStyles } from '../LogControllers/LogStyles';
import processData from '../common/service/dataProcessor';
import { useQuery } from '@tanstack/react-query';
import LogKpiRow from '../LogControllers/LogKpiRow';
import LogDateRangeFilter from '../common/controls/LogDateRangeFilter';

interface Props {
  siteId: string;
  logTitle: string;
  projectId: string;
  isDashboard?: boolean;
  packageFilter?: string[] | null;
}

export default function ConsolEnergyLog(props: Props) {
  const { getAccessToken } = useAuth();
  const { clientAPIURL } = useClient();

  const { data: reportData, isLoading } = useQuery({
    queryKey: ['consolidated-log', props.logTitle, props.projectId],
    queryFn: async () => {
      const accessToken = await getAccessToken();
      const jResponse = await GetConsolidatedLog(props.logTitle, clientAPIURL, accessToken, props.projectId);
      return constructData(jResponse);
    },
  });

  if (isLoading) return <Loader />;

  return reportData && reportData.items && reportData.columns ? (
    <CreateEnergyLogCharts
      LogData={reportData.items}
      LogColumns={reportData.columns}
      siteId={props.siteId}
      logTitle={props.logTitle}
      clientAPIURL={clientAPIURL}
      isDashboard={props.isDashboard}
      packageFilter={props.packageFilter}
    />
  ) : null;
}

interface CreateEnergyLogChartsProps {
  LogData: any;
  LogColumns: any;
  siteId: string;
  logTitle: string;
  clientAPIURL: string;
  isDashboard?: boolean;
  packageFilter?: string[] | null;
}

const CreateEnergyLogCharts: React.FC<CreateEnergyLogChartsProps> = ({ LogData, LogColumns, siteId, logTitle, clientAPIURL, isDashboard, packageFilter }) => {
  const { getAccessToken } = useAuth();

  const ColumnsWithIDColumn = LogColumns && [
    ...LogColumns,
    {
      id: 'CODE_GENERATED_ID',
      title: 'Item ID',
      columnName: 'ID',
      list: '7ae860e9-6c67-46d0-b65e-f2869542a3aa',
      datatype: 'calculated',
      createdAt: 'YOUR_CREATED_AT_TIMESTAMP',
      modifiedAt: 'YOUR_MODIFIED_AT_TIMESTAMP',
      required: false,
      subType: 'Float',
      choiceChoices: null,
      choiceType: null,
      choiceMultiselect: null,
      multiline: false,
      order: 0,
      calculatedFormula: null,
      listNavigation: null,
    },
  ];

  // Memoize processData output — prevents LogDateRangeFilter's useEffect([data]) firing every render
  const updatedData = useMemo(() => {
    const raw = processData(logTitle, LogData);
    if (!packageFilter?.length) return raw;
    return raw.filter((item: any) => packageFilter.includes(item.packageName));
  }, [logTitle, LogData, packageFilter]);

  const [filteredData, setFilteredData] = useState<any[]>(updatedData ?? []);

  async function handleDataUpdate(item: any) {
    if (!item.ID) return;
    try {
      const accessToken = await getAccessToken();
      const result = await getItemById(logTitle, accessToken, clientAPIURL, siteId, item.ID);
      const rawItem = result?.items?.[0];
      if (!rawItem) return;
      const formatDate = (v: any): string => {
        if (!v) return v;
        const adj = new Date(new Date(v).getTime() + 4 * 60 * 60000);
        const d = adj.getDate(), m = adj.getMonth() + 1, y = adj.getFullYear();
        return `${d < 10 ? '0' + d : d}/${m < 10 ? '0' + m : m}/${y}`;
      };
      const formatPeriod = (v: any): string | null => {
        if (!v) return null;
        const adj = new Date(new Date(v).getTime() + 4 * 60 * 60000);
        const m = adj.getMonth() + 1, y = adj.getFullYear();
        return `${m < 10 ? '0' + m : m}-${y}`;
      };
      const updatedItem = { ...rawItem, Date: formatDate(rawItem.Date), Period: formatPeriod(rawItem.Date) };
      setFilteredData(prev => [...prev.filter((o: any) => o.ID !== item.ID), updatedItem]);
    } catch (ex) {
      console.error(ex);
    }
  }

  const chartData = useMemo(() => {
    const colorScaleLine = scaleOrdinal(schemeCategory10);

    const uniquePeriod = [...new Set(filteredData.map((item: any) => item.Period))].filter(Boolean) as string[];
    const uniqueFuelTypes = [...new Set(filteredData.map((item: any) => item.FuelTypeEnergySource))].filter(Boolean) as string[];
    const uniqueUseTypes = [...new Set(filteredData.map((item: any) => item.Use))].filter(Boolean) as string[];

    // PieChartComp contract: data[].name=string (non-null), data[].value=number, data[].color=string
    const sumByFuelType: Record<string, number> = {};
    filteredData.forEach((item: any) => {
      const k = item.FuelTypeEnergySource;
      if (k != null && String(k) !== 'null' && String(k) !== 'undefined') {
        sumByFuelType[String(k)] = (sumByFuelType[String(k)] ?? 0) + (Number(item.Quantitym3) || 0);
      }
    });
    const byFuelTypePieData = Object.entries(sumByFuelType).map(([name, value], idx) => ({
      name,
      value: Math.round(value),
      color: colorType5(idx.toString()),
    }));

    const sumByUse: Record<string, number> = {};
    filteredData.forEach((item: any) => {
      const k = item.Use;
      if (k != null && String(k) !== 'null' && String(k) !== 'undefined') {
        sumByUse[String(k)] = (sumByUse[String(k)] ?? 0) + (Number(item.Quantitym3) || 0);
      }
    });
    const byUsePieData = Object.entries(sumByUse).map(([name, value], idx) => ({
      name,
      value: Math.round(value),
      color: colorType3(idx.toString()),
    }));

    // LineChartScreen contract: labels=string[], datasets[].data=number[], datasets[].color=(opacity)=>string
    const lineDataByFuelType = {
      labels: uniquePeriod,
      datasets: uniqueFuelTypes.map((fuelType, idx) => ({
        data: uniquePeriod.map(period =>
          filteredData
            .filter((item: any) => item.Period === period && item.FuelTypeEnergySource === fuelType)
            .reduce((sum: number, item: any) => sum + (Number(item.Quantitym3) || 0), 0),
        ),
        strokeWidth: 2,
        color: (_opacity = 1) => colorScaleLine(idx.toString()),
      })),
    };
    const legendByFuelType = uniqueFuelTypes.map(name => ({ name }));

    const lineDataByUse = {
      labels: uniquePeriod,
      datasets: uniqueUseTypes.map((use, idx) => ({
        data: uniquePeriod.map(period =>
          filteredData
            .filter((item: any) => item.Period === period && item.Use === use)
            .reduce((sum: number, item: any) => sum + (Number(item.Quantitym3) || 0), 0),
        ),
        strokeWidth: 2,
        color: (_opacity = 1) => colorScaleLine(idx.toString()),
      })),
    };
    const legendByUse = uniqueUseTypes.map(name => ({ name }));

    const totalFuelM3 = Math.round(
      filteredData.reduce((sum: number, item: any) => sum + (Number(item.Quantitym3) || 0), 0),
    );
    const kpiItems = [
      { label: 'Entries',          value: filteredData.length,  color: '#4527A0' },
      { label: 'Total Fuel (m3)', value: totalFuelM3,           color: '#00695C' },
      ...Object.entries(sumByFuelType).map(([name, value], idx) => ({
        label: name,
        value: Math.round(value),
        color: colorType5(idx.toString()),
        unit: 'm³',
      })),
      ...Object.entries(sumByUse).map(([name, value], idx) => ({
        label: name,
        value: Math.round(value),
        color: colorType3(idx.toString()),
        unit: 'm³',
      })),
    ];

    return {
      kpiItems,
      byFuelTypePieData, legendByFuelType, lineDataByFuelType,
      byUsePieData, legendByUse, lineDataByUse,
    };
  }, [filteredData]);

  const {
    kpiItems,
    byFuelTypePieData, legendByFuelType, lineDataByFuelType,
    byUsePieData, legendByUse, lineDataByUse,
  } = chartData;

  const charts = (
    <View style={CommonStyles.container}>
      <View style={[CommonStyles.card, { backgroundColor: '' }]}>
        {/* PieChartComp contract: data[].name=string (non-null), data[].value=number, data[].color=string */}
        <PieChartComp data={byFuelTypePieData} colorPallate={interpolateBlues} subTitle={'(Cumulative)'} ChartTitle={'Fuel Qty (m3) / Type'} />
      </View>
      <View style={[CommonStyles.card, { backgroundColor: '' }]}>
        {/* LineChartScreen contract: labels=string[], datasets[].data=number[], datasets[].color=fn */}
        <LineChartScreen legend={legendByFuelType} data={lineDataByFuelType} label={lineDataByFuelType.labels} subTitle={'(Monthly)'} ChartTitle={'Fuel Qty (m3) / Type'} />
      </View>
      <View style={[CommonStyles.card, { backgroundColor: '' }]}>
        <PieChartComp data={byUsePieData} colorPallate={interpolateGnBu} subTitle={'(Cumulative)'} ChartTitle={'Fuel Qty (m3) / Use'} />
      </View>
      <View style={[CommonStyles.card, { backgroundColor: '' }]}>
        <LineChartScreen legend={legendByUse} data={lineDataByUse} label={lineDataByUse.labels} subTitle={'(Monthly)'} ChartTitle={'Fuel Qty (m3) / Use'} />
      </View>
    </View>
  );

  const kpiRow = (
    <View style={{ zIndex: 200 }}>
      <LogKpiRow items={kpiItems}>
        <LogDateRangeFilter data={updatedData} onFilterChange={setFilteredData} />
      </LogKpiRow>
    </View>
  );

  return (
    <View style={logStyles.logRoot}>
      {isDashboard ? (
        <ScrollView contentContainerStyle={logStyles.scrollViewContentContainer}>
          {kpiRow}
          <View>{charts}</View>
        </ScrollView>
      ) : (
        <TabsProvider defaultIndex={0}>
          <Tabs style={{ backgroundColor: '#fff', height: '100%' }} showLeadingSpace={true}>
            <TabScreen label="Charts" icon="chart-bar">
              <ScrollView contentContainerStyle={logStyles.scrollViewContentContainer}>
                {kpiRow}
                <View>{charts}</View>
              </ScrollView>
            </TabScreen>

            <TabScreen label="List" icon="database">
              <ScrollView contentContainerStyle={logStyles.scrollViewContentContainer}>
                <View>
                  <View style={CommonStyles.container}>
                    <View style={CommonStyles.cardThree}>
                      {filteredData.length > 0 && ColumnsWithIDColumn && (
                        <PaperTableDynamic
                          data={filteredData}
                          columns={ColumnsWithIDColumn}
                          siteId={siteId}
                          logTitle={logTitle}
                          updateData={handleDataUpdate}
                          isConsolidatedTable={true}
                        />
                      )}
                    </View>
                  </View>
                </View>
              </ScrollView>
            </TabScreen>
          </Tabs>
        </TabsProvider>
      )}
    </View>
  );
};
